import type { DocumentType } from '../../../js/api';
import type { Audience } from './workflowAccess';

/**
 * Plain-language answer to "who will be able to see this document?" for the upload
 * dialog, read off the selected type's `allowed_audience_download` — the same list
 * the backend scopes document reads by — from the uploader's point of view.
 */

export type DocumentAudience = 'PROVIDER' | 'HEALTHCARE_ENTITY' | 'STAFF';

const DOCUMENT_AUDIENCES: readonly DocumentAudience[] = ['STAFF', 'HEALTHCARE_ENTITY', 'PROVIDER'];

/** The other parties, as the uploader would name them. */
const OTHER_PARTY: Record<DocumentAudience, string> = {
  STAFF: 'Sendit',
  HEALTHCARE_ENTITY: 'the Healthcare Organization',
  PROVIDER: 'the Clinician',
};

/** The uploader's own party, as they would name themselves. */
const OWN_PARTY: Record<DocumentAudience, string> = {
  STAFF: 'Sendit',
  HEALTHCARE_ENTITY: 'your organization',
  PROVIDER: 'you',
};

export type DocumentVisibility = {
  /** Other parties (not the uploader) who will be able to view the document. */
  canView: string[];
  /** Other parties who will NOT be able to view it. */
  cannotView: string[];
  /** Whether the uploader's own audience may view the type after uploading. */
  uploaderCanView: boolean;
  /** The whole thing as one or two sentences. */
  sentence: string;
};

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** "a", "a and b", "a, b, and c" */
export const joinWithAnd = (items: readonly string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const isDocumentAudience = (audience: Audience): audience is DocumentAudience =>
  audience !== 'UNKNOWN';

/**
 * Who can see a document of `documentType` once `uploader` uploads it. Null until the
 * uploader's audience is known.
 */
export function describeDocumentVisibility(
  documentType: Pick<DocumentType, 'allowed_audience_download'>,
  uploader: Audience
): DocumentVisibility | null {
  if (!isDocumentAudience(uploader)) return null;
  const allowed = new Set(documentType.allowed_audience_download ?? []);
  const others = DOCUMENT_AUDIENCES.filter((audience) => audience !== uploader);
  const canView = others.filter((audience) => allowed.has(audience)).map((a) => OTHER_PARTY[a]);
  const cannotView = others.filter((audience) => !allowed.has(audience)).map((a) => OTHER_PARTY[a]);
  const uploaderCanView = allowed.has(uploader);

  const viewers = [...(uploaderCanView ? [OWN_PARTY[uploader]] : []), ...canView];
  const parts: string[] = [];
  if (viewers.length === 0) {
    parts.push('No one will be able to view this document once it is uploaded.');
  } else if (canView.length === 0) {
    parts.push(`This document will be visible to ${joinWithAnd(viewers)} only.`);
  } else {
    parts.push(`This document will be visible to ${joinWithAnd(viewers)}.`);
  }
  if (cannotView.length) {
    parts.push(`${capitalize(joinWithAnd(cannotView))} will not have access.`);
  }
  if (!uploaderCanView) {
    parts.push(
      uploader === 'PROVIDER'
        ? 'You will not be able to open it yourself after uploading.'
        : `${capitalize(OWN_PARTY[uploader])} will not be able to open it after uploading.`
    );
  }
  return { canView, cannotView, uploaderCanView, sentence: parts.join(' ') };
}
